import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiRequest } from './apiClient';
import './auth.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function passwordStrength(pw) {
  let score = 0;
  if (pw.length >= 8) score += 1;
  if (/[A-Z]/.test(pw)) score += 1;
  if (/[0-9]/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  if (pw.length >= 12) score += 1;
  return score; // 0..5
}

const STRENGTH_LABELS = ['Too weak', 'Weak', 'Fair', 'Good', 'Strong', 'Very strong'];
const STRENGTH_COLORS = ['#dc2626', '#ef4444', '#f59e0b', '#eab308', '#10b981', '#16a34a'];

export default function SignupPage() {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const score = useMemo(() => passwordStrength(password), [password]);
  const meetsPolicy = (
    password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[0-9]/.test(password) &&
    /[^A-Za-z0-9]/.test(password)
  );

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!companyName.trim() || !contactName.trim() || !email.trim()) {
      setError('Please fill in all fields.');
      return;
    }
    if (!meetsPolicy) {
      setError('Password must be at least 8 characters and contain an uppercase letter, a number, and a special character.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      const { ok, data } = await apiRequest('POST', `${BACKEND_URL}/api/auth/signup`, {
        body: {
          company_name: companyName, contact_name: contactName,
          email, password, confirm_password: confirm,
        },
      });
      if (!ok) {
        setError(typeof data.detail === 'string' ? data.detail : 'Signup failed.');
        return;
      }
      // Build the redirect URL. When SendGrid is not yet configured the
      // backend returns the verification_link directly so the verify-email
      // page can render it on-screen for the new user.
      const params = new URLSearchParams({ pending: '1', email });
      if (data.verification_link) params.set('link', data.verification_link);
      navigate('/verify-email?' + params.toString(), { replace: true });
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-shell">
      <div className="auth-card" data-testid="signup-card">
        <div className="auth-brand"><span className="auth-brand-dot"></span> Estatel AI</div>
        <h1 className="auth-title">Create your account</h1>
        <p className="auth-subtitle">Start your 60-day free Agency Pro trial. No credit card required.</p>
        {error && <div className="auth-error" data-testid="signup-error">{error}</div>}
        <form onSubmit={submit}>
          <div className="auth-field">
            <label htmlFor="signup-company">Company name</label>
            <input id="signup-company" type="text" value={companyName} onChange={e => setCompanyName(e.target.value)} required data-testid="signup-company" />
          </div>
          <div className="auth-field">
            <label htmlFor="signup-name">Contact name</label>
            <input id="signup-name" type="text" value={contactName} onChange={e => setContactName(e.target.value)} required data-testid="signup-name" />
          </div>
          <div className="auth-field">
            <label htmlFor="signup-email">Email address</label>
            <input id="signup-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" data-testid="signup-email" />
          </div>
          <div className="auth-field">
            <label htmlFor="signup-password">Password</label>
            <input id="signup-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required autoComplete="new-password" data-testid="signup-password" />
            {password && (
              <div className="auth-strength" data-testid="signup-strength">
                <div className="auth-strength-bar">
                  <div className="auth-strength-fill" style={{ width: `${(score / 5) * 100}%`, background: STRENGTH_COLORS[score] }} />
                </div>
                <div style={{ color: STRENGTH_COLORS[score], fontWeight: 500 }}>{STRENGTH_LABELS[score]}</div>
                <ul style={{ marginTop: 6, paddingLeft: '1.1rem', color: '#64748b', fontSize: '0.8rem' }}>
                  <li style={{ color: password.length >= 8 ? '#16a34a' : '#94a3b8' }}>At least 8 characters</li>
                  <li style={{ color: /[A-Z]/.test(password) ? '#16a34a' : '#94a3b8' }}>One uppercase letter</li>
                  <li style={{ color: /[0-9]/.test(password) ? '#16a34a' : '#94a3b8' }}>One number</li>
                  <li style={{ color: /[^A-Za-z0-9]/.test(password) ? '#16a34a' : '#94a3b8' }}>One special character</li>
                </ul>
              </div>
            )}
          </div>
          <div className="auth-field">
            <label htmlFor="signup-confirm">Confirm password</label>
            <input id="signup-confirm" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required autoComplete="new-password" data-testid="signup-confirm" />
            {confirm && confirm !== password && (
              <div style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '0.35rem' }}>Passwords do not match</div>
            )}
          </div>
          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%', background: submitting ? '#94a3b8' : '#0073EA', color: '#fff',
              border: 'none', borderRadius: 6, padding: '0.7rem', fontSize: '0.95rem',
              fontWeight: 500, cursor: submitting ? 'not-allowed' : 'pointer', marginTop: '0.5rem',
            }}
            data-testid="signup-submit"
          >
            {submitting ? 'Creating account…' : 'Create account'}
          </button>
        </form>
        <div className="auth-footer">
          Already have an account? <Link to="/login" data-testid="signup-login-link">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
